import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { segmentClass, segmentTrackClass } from '@charitystack/shared';

// Frequency selector: Once/Repeat toggle + recurring frequency button bar.
// Unified layout for all formDisplayMode values (embed, overlay, hosted page).

const freqMap = {
  '': '',
  ONE_TIME: 'One-Time',
  DAILY: 'Day',
  WEEKLY: 'Week',
  MONTHLY: 'Month',
  ANNUALLY: 'Year',
};

const freqsLenToWidth = {
  1: 'w-full',
  2: 'w-1/2',
  3: 'w-1/3',
  4: 'w-1/4',
  5: 'w-1/5',
  6: 'w-1/6',
};

// Donor redesign (CON-516, SPEC §C Segmented control): every segmented
// control here — Once/Repeat and the "Repeats every" bar — is the one shared
// recipe: a 40 sunken track with a white 32 pill (card shadow, no border).
// The selected pill is white for every org, so brand colours no longer paint.
const REPEATS_LABEL_CLASS =
  'pb-[var(--cs-space-2)] font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-medium text-[color:var(--cs-text-secondary)]';

// ─── Frequency Layout (Once/Repeat toggle + button bar) ──────────────────────

function DynamicFrequencyLayout({
  frequencies,
  frequency,
  applyFrequency,
  elementID,
  hasError,
  donationType,
  lastRecurringRef,
  setDonationType,
}) {
  const recurringFrequencies = frequencies.filter((freq) => freq !== 'ONE_TIME');
  const isRepeat = donationType === 'Repeat';
  // A11-003: the collapsed Repeat row is clipped to zero height, not unmounted,
  // so its buttons stayed in the tab order while "Once" was selected. `inert`
  // takes the whole row out of focus and the accessibility tree. Written as an
  // attribute from a layout effect rather than a JSX prop: React 18 has no
  // `inert` prop, and the string-valued workaround turns into `false` on 19.
  const recurringRowRef = useRef(null);
  const hasOnceToggle = frequencies.includes('ONE_TIME');
  useLayoutEffect(() => {
    recurringRowRef.current?.toggleAttribute('inert', !isRepeat);
  }, [isRepeat, hasOnceToggle]);

  // If ONE_TIME is not present, show only the Repeats Every button bar
  if (!hasOnceToggle) {
    return (
      <div>
        <div>
          <div className={REPEATS_LABEL_CLASS}>
            Repeats every
          </div>
          <span
            className={`dynamic-frequency-grid ${segmentTrackClass(hasError)} ${
              frequencies.length === 1 ? 'single-frequency' : ''
            }`}
            id={`${elementID}-frequencies`}
          >
            {frequencies.map((buttonFreq, i) => {
              const isSelected = buttonFreq === frequency;
              return (
                <button
                  key={buttonFreq}
                  type="button"
                  className={`${segmentClass(isSelected)} ${frequencies.length === 1 ? 'w-full' : freqsLenToWidth[frequencies.length]}`}
                  style={frequencies.length === 1 ? { width: '100%' } : undefined}
                  onClick={() => applyFrequency(buttonFreq)}
                  id={`${elementID}-frequencies-button-${i}`}
                >
                  {freqMap[buttonFreq]}
                </button>
              );
            })}
          </span>
        </div>
      </div>
    );
  }

  // ONE_TIME + recurring: Once/Repeat toggle with collapsible recurring section
  return (
    <div>
      <span
        className={segmentTrackClass(hasError)}
        id={`${elementID}-donation-type`}
      >
        {['Once', 'Repeat'].map((type, i) => {
          const isSelected = type === donationType;
          return (
            <button
              key={type}
              type="button"
              className={`${segmentClass(isSelected)} w-1/2`}
              onClick={() => {
                setDonationType(type);
                if (type === 'Once') {
                  applyFrequency('ONE_TIME');
                } else if (type === 'Repeat') {
                  // Back to the repeat frequency the donor had before Once (QA #9), else the first.
                  const availableFrequencies = frequencies.filter((freq) => freq !== 'ONE_TIME');
                  if (availableFrequencies.length > 0) {
                    const lastRecurring = lastRecurringRef?.current;
                    applyFrequency(availableFrequencies.includes(lastRecurring)
                      ? lastRecurring
                      : availableFrequencies[0]);
                  }
                }
              }}
              id={`${elementID}-donation-type-button-${i}`}
            >
              {type}
            </button>
          );
        })}
      </span>

      <div
        ref={recurringRowRef}
        className="w-full grid transition-[grid-template-rows] duration-[var(--cs-motion-base)] ease-in-out"
        style={{ gridTemplateRows: isRepeat ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden min-h-0">
          <div>
            {/* 20 below the Once/Repeat track, inside the collapsing row (Paper 2JU-0). */}
            <div className={`pt-[var(--cs-space-5)] ${REPEATS_LABEL_CLASS}`}>
              Repeats every
            </div>
            <span
              className={`dynamic-frequency-grid ${segmentTrackClass(hasError)} ${
                recurringFrequencies.length === 1 ? 'single-frequency' : ''
              }`}
              id={`${elementID}-frequencies`}
            >
              {recurringFrequencies.map((buttonFreq, i) => {
                const isSelected = buttonFreq === frequency;
                return (
                  <button
                    key={buttonFreq}
                    type="button"
                    className={`${segmentClass(isSelected)} ${recurringFrequencies.length === 1 ? 'w-full' : freqsLenToWidth[recurringFrequencies.length]}`}
                    style={recurringFrequencies.length === 1 ? { width: '100%' } : undefined}
                    onClick={() => applyFrequency(buttonFreq)}
                    id={`${elementID}-frequencies-button-${i}`}
                  >
                    {freqMap[buttonFreq]}
                  </button>
                );
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function DonationFrequencies({
  frequencies,
  frequency,
  setFrequency,
  primaryColor,
  darkColor,
  secondaryColor,
  elementID,
  brandColors,
  selectedFrequency,
  setSelectedFrequency,
  hasError,
  onFrequencyChange,
  donationType = 'Once',
  setDonationType = () => {},
  formDisplayMode = 'dynamic',
}) {
  // The last repeat frequency the donor was on, so Once → Repeat returns to it.
  // Updated in an effect (never during render); read at click time.
  const lastRecurringRef = useRef(frequency && frequency !== 'ONE_TIME' ? frequency : null);
  useEffect(() => {
    if (frequency && frequency !== 'ONE_TIME') lastRecurringRef.current = frequency;
  }, [frequency]);

  // Sync donationType state with frequency
  useEffect(() => {
    if (frequency === 'ONE_TIME') {
      setDonationType('Once');
    } else if (frequency && frequency !== 'ONE_TIME') {
      setDonationType('Repeat');
    }
  }, [frequency, setDonationType]);

  const applyFrequency = (nextFrequency) => {
    setFrequency(nextFrequency);
    setSelectedFrequency(nextFrequency);
    setDonationType(nextFrequency === 'ONE_TIME' ? 'Once' : 'Repeat');
    onFrequencyChange?.();
  };

  // Nothing to render if no frequencies or only ONE_TIME
  if (frequencies.length === 0 || (frequencies.length === 1 && frequencies[0] === 'ONE_TIME')) {
    return <></>;
  }

  return (
    <DynamicFrequencyLayout
      frequencies={frequencies}
      frequency={frequency}
      applyFrequency={applyFrequency}
      elementID={elementID}
      hasError={hasError}
      donationType={donationType}
      setDonationType={setDonationType}
      lastRecurringRef={lastRecurringRef}
    />
  );
}
